import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  UserCheck,
  Pencil,
  Plus,
  Check,
  X,
  Sparkles,
  Briefcase,
  Filter,
  Trash2,
  Link2,
  CalendarDays,
  Clock3,
  ShieldCheck,
  BadgeCheck,
  Download,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { api, get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDateTime } from '@/lib/format';

// FV `kind` is now a free-form catalog code (per-society) rather than a fixed
// enum. The valid values come from GET /v1/fv-kinds. We still constrain the
// shape (lowercase + min/max length) client-side so we can show useful
// validation; the server is the source of truth for "does this code exist".
type Kind = string;
type Status = 'pending' | 'verified' | 'rejected' | 'expired';
type PoliceStatus = 'not_started' | 'pending' | 'verified' | 'rejected';
type IdProofType = 'aadhaar' | 'pan' | 'driving_licence' | 'passport' | 'other';

interface FrequentVisitor {
  id: string;
  society_id: string;
  full_name: string;
  phone: string;
  kind: Kind;
  status: Status;
  photo_url: string | null;
  photo_storage_key: string | null;
  id_proof_type: IdProofType | null;
  id_proof_last4: string | null;
  id_proof_url: string | null;
  id_proof_storage_key: string | null;
  police_verification_status: PoliceStatus;
  police_verification_url: string | null;
  police_verification_storage_key: string | null;
  police_verified_at: string | null;
  id_card_qr_token: string | null;
  id_card_issued_at: string | null;
  alt_phone: string | null;
  dob: string | null;
  permanent_address: string | null;
  current_address: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  notes: string | null;
  rejection_reason: string | null;
  verified_at: string | null;
  created_at: string;
}

interface ListResponse {
  items: FrequentVisitor[];
  next_cursor: string | null;
}

type ApprovalStatus = 'auto_approved' | 'pending_resident' | 'approved' | 'rejected';

interface Assignment {
  id: string;
  frequent_visitor_id: string;
  unit_id: string;
  added_by_resident_id: string | null;
  days_of_week: string | null;
  time_start: string | null;
  time_end: string | null;
  approval_status: ApprovalStatus;
  approval_requested_by_admin_id: string | null;
  approval_requested_at: string | null;
  approval_decided_by_resident_id: string | null;
  approval_decided_at: string | null;
  approval_rejection_reason: string | null;
  active: boolean;
  notes: string | null;
  created_at: string;
  unit: { id: string; number: string; block: { id: string; name: string } | null };
  added_by: { id: string; full_name: string; phone: string } | null;
}

const APPROVAL_STATUS_LABEL: Record<ApprovalStatus, string> = {
  auto_approved: 'Auto-approved',
  approved: 'Approved',
  pending_resident: 'Pending resident',
  rejected: 'Rejected'
};

const APPROVAL_STATUS_VARIANT: Record<ApprovalStatus, 'ok' | 'warn' | 'alert' | 'muted'> = {
  auto_approved: 'ok',
  approved: 'ok',
  pending_resident: 'warn',
  rejected: 'alert'
};

interface AssignmentListResponse {
  items: Assignment[];
}

interface UnitOption {
  id: string;
  block_id: string | null;
  number: string;
}
interface BlockOption {
  id: string;
  name: string;
}

const DAYS_OF_WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const DAY_LABEL: Record<string, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun'
};

const AssignmentSchema = z.object({
  unit_id: z.string().uuid('Select a flat'),
  days_of_week: z.string().optional(),
  time_start: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM (24h)')
    .optional()
    .or(z.literal('')),
  time_end: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM (24h)')
    .optional()
    .or(z.literal(''))
});
type AssignmentForm = z.infer<typeof AssignmentSchema>;

/**
 * KindRow mirrors `/v1/fv-kinds` catalog response. Society-curated list of
 * helper roles + per-kind verification policy. We fetch ALL rows (active +
 * inactive) so historical FVs whose `kind` references a deactivated code can
 * still resolve to a human-readable label.
 */
type VerificationLevel = 'recommended' | 'light';
interface KindRow {
  id: string;
  code: string;
  label: string;
  verification_level: VerificationLevel;
  hard_block_id_card: boolean;
  issue_id_card: boolean;
  manual_capture_photo: boolean;
  sort_order: number;
  active: boolean;
}

const VERIFICATION_HINT: Record<VerificationLevel, string> = {
  recommended:
    'Police verification recommended — this role enters the home or handles valuables.',
  light: 'Photo + phone are usually enough — daily delivery / open-area role.'
};

const STATUS_VARIANT: Record<Status, 'warn' | 'ok' | 'alert' | 'muted'> = {
  pending: 'warn',
  verified: 'ok',
  rejected: 'alert',
  expired: 'muted'
};

const POLICE_VARIANT: Record<PoliceStatus, 'warn' | 'ok' | 'alert' | 'muted'> = {
  not_started: 'muted',
  pending: 'warn',
  verified: 'ok',
  rejected: 'alert'
};

const POLICE_LABEL: Record<PoliceStatus, string> = {
  not_started: 'Not started',
  pending: 'Under review',
  verified: 'Police verified',
  rejected: 'Police rejected'
};

const ID_PROOF_LABEL: Record<IdProofType, string> = {
  aadhaar: 'Aadhaar',
  pan: 'PAN',
  driving_licence: 'Driving licence',
  passport: 'Passport',
  other: 'Other'
};
const ID_PROOF_OPTIONS: IdProofType[] = ['aadhaar', 'pan', 'driving_licence', 'passport', 'other'];
const FV_IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const FV_IMAGE_ACCEPT = FV_IMAGE_MIME.join(',');
const FV_DOC_MIME = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const FV_DOC_ACCEPT = FV_DOC_MIME.join(',');

const CreateSchema = z.object({
  full_name: z.string().min(2, 'Name required').max(120),
  phone: z.string().regex(/^\+?[1-9]\d{6,14}$/, 'E.164 format'),
  // kind is a free-form catalog code — server validates against the per-society
  // FrequentVisitorKindCatalog. Format-only guard here (matches catalog API).
  kind: z
    .string()
    .min(2, 'Pick a helper category')
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/, 'Invalid helper category code'),
  id_proof_type: z
    .enum(['aadhaar', 'pan', 'driving_licence', 'passport', 'other'] as [IdProofType, ...IdProofType[]])
    .optional()
    .or(z.literal('')),
  id_proof_last4: z.string().max(8).optional().or(z.literal('')),
  alt_phone: z
    .string()
    .regex(/^\+?[1-9]\d{6,14}$/, 'E.164 format')
    .optional()
    .or(z.literal('')),
  dob: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD').optional().or(z.literal('')),
  permanent_address: z.string().max(500).optional().or(z.literal('')),
  current_address: z.string().max(500).optional().or(z.literal('')),
  emergency_contact_name: z.string().max(120).optional().or(z.literal('')),
  emergency_contact_phone: z
    .string()
    .regex(/^\+?[1-9]\d{6,14}$/, 'E.164 format')
    .optional()
    .or(z.literal('')),
  notes: z.string().max(500).optional()
});
type CreateForm = z.infer<typeof CreateSchema>;

type ViewTab = 'all' | 'queue';

/**
 * Fetches an authenticated FV blob (photo / id-proof / police-verification)
 * via fetch+bearer and returns a temporary object URL. Re-fetches when the
 * cacheBust key changes; revokes the URL on unmount.
 */
function useFvBlobUrl(
  fvId: string | null | undefined,
  kind: 'photo' | 'id-proof' | 'police-verification',
  cacheBust: string | null | undefined
): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!fvId || !cacheBust) {
      setUrl(null);
      return;
    }
    let revoked = false;
    let createdUrl: string | null = null;
    api
      .get<Blob>(`/frequent-visitors/${fvId}/${kind}`, { responseType: 'blob' })
      .then((res) => {
        if (revoked) return;
        createdUrl = URL.createObjectURL(res.data);
        setUrl(createdUrl);
      })
      .catch(() => {
        if (!revoked) setUrl(null);
      });
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [fvId, kind, cacheBust]);
  return url;
}

function initialsFor(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function FvAvatar({ fv, size = 32 }: { fv: FrequentVisitor; size?: number }): JSX.Element {
  const photoUrl = useFvBlobUrl(fv.id, 'photo', fv.photo_storage_key);
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={fv.full_name}
        className="shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full bg-vt-primary-100 text-vt-12 font-semibold text-vt-primary"
      style={{ width: size, height: size }}
    >
      {initialsFor(fv.full_name)}
    </span>
  );
}

export function FrequentVisitorsPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Confirm dialogs (replace native window.confirm/prompt).
  const [rejectTarget, setRejectTarget] = useState<FrequentVisitor | null>(null);
  const [removeTarget, setRemoveTarget] = useState<FrequentVisitor | null>(null);
  const [removeAssignmentTarget, setRemoveAssignmentTarget] = useState<Assignment | null>(null);
  const [statusFilter, setStatusFilter] = useState<Status | ''>('');
  const [viewTab, setViewTab] = useState<ViewTab>('all');
  const [selectedFvId, setSelectedFvId] = useState<string | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [approvalFilter, setApprovalFilter] = useState<ApprovalStatus | ''>('');
  const queryClient = useQueryClient();

  // File state for the create/edit dialog
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [idProofFile, setIdProofFile] = useState<File | null>(null);
  const [policeDocFile, setPoliceDocFile] = useState<File | null>(null);
  const [policeDecision, setPoliceDecision] = useState<'pending' | 'verified' | 'rejected'>('pending');

  // ID-card dialog state
  const [idCardFvId, setIdCardFvId] = useState<string | null>(null);
  const [idCardData, setIdCardData] = useState<{ qr_data_url: string; id_card_url: string } | null>(null);
  // Object URL for the full SVG card preview (fetched via auth-blob).
  const [idCardPreviewUrl, setIdCardPreviewUrl] = useState<string | null>(null);

  const fvFiltersHash = useMemo(
    () => JSON.stringify([statusFilter, viewTab]),
    [statusFilter, viewTab]
  );
  const [fvCursor, setFvCursor] = useState<string | null>(null);
  useEffect(() => {
    setFvCursor(null);
  }, [fvFiltersHash]);
  const list = useQuery({
    queryKey: ['frequent-visitors', statusFilter, viewTab, fvCursor],
    queryFn: () =>
      get<ListResponse>('/frequent-visitors', {
        params: {
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(viewTab === 'queue' ? { verification_queue: 'true' } : {}),
          limit: 50,
          ...(fvCursor ? { cursor: fvCursor } : {})
        }
      })
  });
  const fvPaginator = useCursorAccumulator<FrequentVisitor>(list.data, fvCursor, fvFiltersHash);

  // ─── FV kinds catalog (per-society) ───
  // Source of truth for the picker, the table "Kind" column labels, and the
  // verification-level hint shown in the create/edit dialog. We fetch ALL rows
  // (active + inactive) so historical FVs whose kind has been deactivated can
  // still resolve to a label; the dialog picker only offers active rows.
  const kindsQuery = useQuery({
    queryKey: ['fv-kinds'],
    queryFn: () => get<{ items: KindRow[] }>('/fv-kinds')
  });
  const kindItems = kindsQuery.data?.items ?? [];
  const labelByCode = useMemo<Record<string, string>>(
    () => Object.fromEntries(kindItems.map((k) => [k.code, k.label])),
    [kindItems]
  );
  const levelByCode = useMemo<Record<string, VerificationLevel>>(
    () => Object.fromEntries(kindItems.map((k) => [k.code, k.verification_level])),
    [kindItems]
  );
  const sortedKinds = useMemo(
    () =>
      [...kindItems]
        .filter((k) => k.active)
        .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label)),
    [kindItems]
  );

  const items = fvPaginator.items;
  const pendingCount = items.filter((i) => i.status === 'pending').length;
  const policePendingCount = items.filter((i) => i.police_verification_status === 'pending').length;
  const verifiedCount = items.filter((i) => i.status === 'verified').length;

  const editingFv = useMemo(
    () => (editingId ? items.find((i) => i.id === editingId) ?? null : null),
    [editingId, items]
  );

  const form = useForm<CreateForm>({
    resolver: zodResolver(CreateSchema),
    defaultValues: {
      full_name: '',
      phone: '',
      // Catalog is loaded async; the open-dialog effect below seeds the picker
      // with the first active code once available.
      kind: '',
      id_proof_type: '',
      id_proof_last4: '',
      alt_phone: '',
      dob: '',
      permanent_address: '',
      current_address: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      notes: ''
    }
  });

  const resetUploadState = (): void => {
    setPhotoFile(null);
    setIdProofFile(null);
    setPoliceDocFile(null);
    setPoliceDecision('pending');
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
      setPhotoPreviewUrl(null);
    }
  };

  const onPhotoFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0] ?? null;
    if (f && !FV_IMAGE_MIME.includes(f.type)) {
      toast.error('Photo must be JPEG, PNG or WebP');
      e.target.value = '';
      return;
    }
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoFile(f);
    setPhotoPreviewUrl(f ? URL.createObjectURL(f) : null);
  };

  const onIdProofFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0] ?? null;
    if (f && !FV_IMAGE_MIME.includes(f.type)) {
      toast.error('ID proof must be JPEG, PNG or WebP');
      e.target.value = '';
      return;
    }
    setIdProofFile(f);
  };

  const onPoliceFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0] ?? null;
    if (f && !FV_DOC_MIME.includes(f.type)) {
      toast.error('Police doc must be JPEG, PNG, WebP, or PDF');
      e.target.value = '';
      return;
    }
    setPoliceDocFile(f);
  };

  const create = useMutation({
    mutationFn: async (values: CreateForm) => {
      const body: Record<string, unknown> = {
        full_name: values.full_name,
        phone: values.phone,
        kind: values.kind
      };
      if (values.notes?.trim()) body.notes = values.notes;
      if (values.alt_phone?.trim()) body.alt_phone = values.alt_phone.trim();
      if (values.dob) body.dob = values.dob;
      if (values.permanent_address?.trim()) body.permanent_address = values.permanent_address.trim();
      if (values.current_address?.trim()) body.current_address = values.current_address.trim();
      if (values.emergency_contact_name?.trim())
        body.emergency_contact_name = values.emergency_contact_name.trim();
      if (values.emergency_contact_phone?.trim())
        body.emergency_contact_phone = values.emergency_contact_phone.trim();
      // ID-proof metadata only travels with the create body when there's no
      // file upload (file upload route writes both metadata + storage_key).
      if (!idProofFile) {
        if (values.id_proof_type) body.id_proof_type = values.id_proof_type;
        if (values.id_proof_last4?.trim()) body.id_proof_last4 = values.id_proof_last4.trim();
      }
      const fv = await post<FrequentVisitor>('/frequent-visitors', body);

      if (photoFile) {
        const fd = new FormData();
        fd.append('file', photoFile);
        await api.post(`/frequent-visitors/${fv.id}/photo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      if (idProofFile) {
        if (!values.id_proof_type) {
          throw new Error('Select an ID proof type before uploading the scan');
        }
        const fd = new FormData();
        fd.append('file', idProofFile);
        fd.append('id_proof_type', values.id_proof_type);
        if (values.id_proof_last4?.trim()) fd.append('id_proof_last4', values.id_proof_last4.trim());
        await api.post(`/frequent-visitors/${fv.id}/id-proof`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      if (policeDocFile) {
        const fd = new FormData();
        fd.append('file', policeDocFile);
        fd.append('decision', policeDecision);
        await api.post(`/frequent-visitors/${fv.id}/police-verification`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      return fv;
    },
    onSuccess: (fv) => {
      toast.success(`${fv.full_name} added (${fv.status})`);
      void queryClient.invalidateQueries({ queryKey: ['frequent-visitors'] });
      setOpen(false);
      form.reset();
      resetUploadState();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const update = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: CreateForm }) => {
      const body: Record<string, unknown> = {
        full_name: values.full_name,
        phone: values.phone,
        kind: values.kind,
        notes: values.notes?.trim() ? values.notes : null,
        alt_phone: values.alt_phone?.trim() ? values.alt_phone.trim() : null,
        dob: values.dob ? values.dob : null,
        permanent_address: values.permanent_address?.trim() ? values.permanent_address.trim() : null,
        current_address: values.current_address?.trim() ? values.current_address.trim() : null,
        emergency_contact_name: values.emergency_contact_name?.trim()
          ? values.emergency_contact_name.trim()
          : null,
        emergency_contact_phone: values.emergency_contact_phone?.trim()
          ? values.emergency_contact_phone.trim()
          : null
      };
      if (!idProofFile) {
        if (values.id_proof_type) body.id_proof_type = values.id_proof_type;
        if (values.id_proof_last4 !== undefined) {
          body.id_proof_last4 = values.id_proof_last4.trim() || null;
        }
      }
      const fv = await patch<FrequentVisitor>(`/frequent-visitors/${id}`, body);

      if (photoFile) {
        const fd = new FormData();
        fd.append('file', photoFile);
        await api.post(`/frequent-visitors/${id}/photo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      if (idProofFile) {
        if (!values.id_proof_type) {
          throw new Error('Select an ID proof type before uploading the scan');
        }
        const fd = new FormData();
        fd.append('file', idProofFile);
        fd.append('id_proof_type', values.id_proof_type);
        if (values.id_proof_last4?.trim()) fd.append('id_proof_last4', values.id_proof_last4.trim());
        await api.post(`/frequent-visitors/${id}/id-proof`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      if (policeDocFile) {
        const fd = new FormData();
        fd.append('file', policeDocFile);
        fd.append('decision', policeDecision);
        await api.post(`/frequent-visitors/${id}/police-verification`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      return fv;
    },
    onSuccess: (fv) => {
      toast.success(`${fv.full_name} updated`);
      void queryClient.invalidateQueries({ queryKey: ['frequent-visitors'] });
      setEditingId(null);
      setOpen(false);
      form.reset();
      resetUploadState();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  useEffect(() => {
    if (editingFv && open) {
      form.reset({
        full_name: editingFv.full_name,
        phone: editingFv.phone,
        kind: editingFv.kind,
        id_proof_type: editingFv.id_proof_type ?? '',
        id_proof_last4: editingFv.id_proof_last4 ?? '',
        alt_phone: editingFv.alt_phone ?? '',
        dob: editingFv.dob ? editingFv.dob.slice(0, 10) : '',
        permanent_address: editingFv.permanent_address ?? '',
        current_address: editingFv.current_address ?? '',
        emergency_contact_name: editingFv.emergency_contact_name ?? '',
        emergency_contact_phone: editingFv.emergency_contact_phone ?? '',
        notes: editingFv.notes ?? ''
      });
    } else if (!open && !editingId) {
      form.reset({
        full_name: '',
        phone: '',
        // Default to first active kind once the catalog has loaded; otherwise
        // leave blank so zod's min(2) blocks submit until catalog arrives.
        kind: sortedKinds[0]?.code ?? '',
        id_proof_type: '',
        id_proof_last4: '',
        alt_phone: '',
        dob: '',
        permanent_address: '',
        current_address: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        notes: ''
      });
      resetUploadState();
    } else if (open && !editingId) {
      // Dialog opened for "add" but catalog wasn't loaded when initial reset
      // ran — patch in the first kind code now without resetting other fields.
      if (!form.getValues('kind') && sortedKinds[0]) {
        form.setValue('kind', sortedKinds[0].code, { shouldValidate: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingFv, open, editingId, sortedKinds]);

  const onSubmit = (v: CreateForm) => {
    if (editingId) update.mutate({ id: editingId, values: v });
    else create.mutate(v);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditingId(null);
    resetUploadState();
  };

  const decide = useMutation({
    mutationFn: ({ id, decision, reason }: { id: string; decision: 'verify' | 'reject'; reason?: string }) =>
      patch<FrequentVisitor>(`/frequent-visitors/${id}/decision`, {
        decision,
        ...(reason ? { rejection_reason: reason } : {})
      }),
    onSuccess: (fv) => {
      toast.success(`${fv.full_name} ${fv.status}`);
      void queryClient.invalidateQueries({ queryKey: ['frequent-visitors'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  /**
   * Quick "Mark police verified / rejected" without a new doc upload — sends
   * a 1-byte placeholder for the file field since the multipart endpoint
   * requires one. Used from the row action; the full upload flow lives in
   * the dialog.
   */
  const markPolice = useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: 'verified' | 'rejected' }) => {
      const fv = items.find((i) => i.id === id);
      if (!fv?.police_verification_storage_key) {
        throw new Error('Upload the police-verification document first');
      }
      // Re-upload a tiny placeholder is awkward; instead we fetch the existing
      // blob and re-post it to flip the decision. Server overwrites storage_key
      // each call — this is a known limitation noted for follow-up: add a
      // PATCH-decision-only variant.
      const blob = await api
        .get<Blob>(`/frequent-visitors/${id}/police-verification`, { responseType: 'blob' })
        .then((r) => r.data);
      const fd = new FormData();
      fd.append('file', blob, 'police-verification');
      fd.append('decision', decision);
      return api.post(`/frequent-visitors/${id}/police-verification`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    onSuccess: (_r, vars) => {
      toast.success(`Police verification ${vars.decision}`);
      void queryClient.invalidateQueries({ queryKey: ['frequent-visitors'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const issueIdCard = useMutation({
    mutationFn: ({ id, regenerate }: { id: string; regenerate: boolean }) =>
      post<{ qr_token: string; qr_data_url: string; id_card_url: string; regenerated: boolean }>(
        `/frequent-visitors/${id}/id-card`,
        { regenerate }
      ),
    onSuccess: async (res, vars) => {
      toast.success(res.regenerated ? 'New ID card issued' : 'ID card ready');
      setIdCardFvId(vars.id);
      setIdCardData({ qr_data_url: res.qr_data_url, id_card_url: res.id_card_url });
      // Fetch the rich SVG card (photo + name + kind + flats + QR) for the
      // dialog preview. Auth-gated → use the api client + blob → object URL.
      try {
        const svgRes = await api.get<Blob>(`/frequent-visitors/${vars.id}/id-card.svg`, {
          responseType: 'blob'
        });
        if (idCardPreviewUrl) URL.revokeObjectURL(idCardPreviewUrl);
        setIdCardPreviewUrl(URL.createObjectURL(svgRes.data));
      } catch {
        // Non-fatal — fall back to QR-only preview.
        setIdCardPreviewUrl(null);
      }
      void queryClient.invalidateQueries({ queryKey: ['frequent-visitors'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  /**
   * Download the FV ID card. `format='svg'` returns the composed card with
   * photo + name + flat + QR (recommended for printing). `format='png'` is
   * the legacy bare-QR PNG (kept for compatibility / scanner kiosk imports
   * that can't parse SVG).
   */
  const downloadIdCard = async (
    fvId: string,
    name: string,
    format: 'svg' | 'png' = 'svg'
  ): Promise<void> => {
    try {
      const res = await api.get<Blob>(`/frequent-visitors/${fvId}/id-card.${format}`, {
        responseType: 'blob'
      });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      const safe = name.replace(/[^A-Za-z0-9-]+/g, '-').slice(0, 40) || 'staff';
      a.download = `hooddesk-id-${safe}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const remove = useMutation({
    mutationFn: (id: string) => del<void>(`/frequent-visitors/${id}`),
    onSuccess: () => {
      toast.success('Removed from register');
      void queryClient.invalidateQueries({ queryKey: ['frequent-visitors'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ─── Assignments (drill-down) ───
  const selectedFv = useMemo(
    () => (selectedFvId ? items.find((i) => i.id === selectedFvId) ?? null : null),
    [selectedFvId, items]
  );

  const assignmentsQuery = useQuery({
    queryKey: ['fv-assignments', selectedFvId],
    queryFn: () =>
      get<AssignmentListResponse>(`/frequent-visitors/${selectedFvId}/assignments`),
    enabled: Boolean(selectedFvId)
  });

  const blocksQuery = useQuery({
    queryKey: ['society', 'blocks'],
    queryFn: () => get<{ items: BlockOption[] }>('/society/blocks'),
    enabled: assignOpen
  });
  const unitsQuery = useQuery({
    queryKey: ['society', 'units', 'all-fv'],
    queryFn: () => get<{ items: UnitOption[] }>('/society/units', { params: { limit: 200 } }),
    enabled: assignOpen
  });

  const blockNameById = useMemo(() => {
    const map = new Map<string, string>();
    (blocksQuery.data?.items ?? []).forEach((b) => map.set(b.id, b.name));
    return map;
  }, [blocksQuery.data]);

  const unitOptions = useMemo(() => {
    const units = unitsQuery.data?.items ?? [];
    return [...units]
      .map((u) => ({
        id: u.id,
        label: `${u.block_id ? blockNameById.get(u.block_id) ?? '?' : '—'} / ${u.number}`,
        sortKey: `${u.block_id ? blockNameById.get(u.block_id) ?? 'zzz' : 'zzz'}-${u.number}`
      }))
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey, undefined, { numeric: true }));
  }, [unitsQuery.data, blockNameById]);

  const assignForm = useForm<AssignmentForm>({
    resolver: zodResolver(AssignmentSchema),
    defaultValues: { unit_id: '', days_of_week: '', time_start: '', time_end: '' }
  });

  const createAssignment = useMutation({
    mutationFn: (values: AssignmentForm) =>
      post<Assignment, Partial<AssignmentForm>>(
        `/frequent-visitors/${selectedFvId}/assignments`,
        {
          unit_id: values.unit_id,
          ...(selectedDays.length > 0 && selectedDays.length < 7
            ? { days_of_week: selectedDays.join(',') }
            : selectedDays.length === 7
            ? { days_of_week: 'all' }
            : {}),
          ...(values.time_start ? { time_start: values.time_start } : {}),
          ...(values.time_end ? { time_end: values.time_end } : {})
        }
      ),
    onSuccess: (assignment) => {
      if (assignment.approval_status === 'pending_resident') {
        toast.success('Assignment created, awaiting resident approval');
      } else {
        toast.success('Flat linked');
      }
      void queryClient.invalidateQueries({ queryKey: ['fv-assignments', selectedFvId] });
      setAssignOpen(false);
      setSelectedDays([]);
      assignForm.reset();
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const removeAssignment = useMutation({
    mutationFn: (aid: string) =>
      del<void>(`/frequent-visitors/${selectedFvId}/assignments/${aid}`),
    onSuccess: () => {
      toast.success('Assignment removed');
      void queryClient.invalidateQueries({ queryKey: ['fv-assignments', selectedFvId] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const toggleActive = useMutation({
    mutationFn: ({ aid, active }: { aid: string; active: boolean }) =>
      patch<Assignment>(`/frequent-visitors/${selectedFvId}/assignments/${aid}`, { active }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['fv-assignments', selectedFvId] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const closeDrillDown = () => {
    setSelectedFvId(null);
    setAssignOpen(false);
    setSelectedDays([]);
    assignForm.reset();
  };

  const toggleDay = (d: string) => {
    setSelectedDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  };

  const formDialog = (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
      <DialogTrigger asChild>
        <Button size="sm" onClick={() => setEditingId(null)}>
          <Plus className="h-4 w-4" /> Add frequent visitor
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editingId ? 'Edit recurring staff' : 'Add recurring staff'}</DialogTitle>
          <DialogDescription>
            {editingId
              ? 'Update profile, KYC, and police verification.'
              : 'Admin-added staff are auto-verified. Resident nominations land as pending until you review.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-vt-4">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="full_name">Full name</Label>
            <Input
              id="full_name"
              placeholder="Geeta Devi"
              autoFocus
              aria-invalid={Boolean(form.formState.errors.full_name) || undefined}
              {...form.register('full_name')}
            />
            {form.formState.errors.full_name ? (
              <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.full_name.message}</p>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                placeholder="+919812345678"
                aria-invalid={Boolean(form.formState.errors.phone) || undefined}
                {...form.register('phone')}
              />
              {form.formState.errors.phone ? (
                <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.phone.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="kind">Kind</Label>
              <Select id="kind" {...form.register('kind')} disabled={kindsQuery.isLoading}>
                {kindsQuery.isLoading ? (
                  <option value="">Loading categories…</option>
                ) : sortedKinds.length === 0 ? (
                  <option value="">No categories — add via Helper categories</option>
                ) : (
                  sortedKinds.map((k) => (
                    <option key={k.code} value={k.code}>
                      {k.label}
                    </option>
                  ))
                )}
              </Select>
              {form.formState.errors.kind ? (
                <p className="text-vt-12 text-vt-alert" role="alert">
                  {form.formState.errors.kind.message}
                </p>
              ) : null}
            </div>
          </div>

          {/* ───── Personal & contact ───── */}
          <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
            <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Personal &amp; contact
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="fv-alt-phone">Alternate phone</Label>
                <Input id="fv-alt-phone" placeholder="+91…" {...form.register('alt_phone')} />
                {form.formState.errors.alt_phone ? (
                  <p className="text-vt-12 text-vt-alert">
                    {form.formState.errors.alt_phone.message}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="fv-dob">Date of birth</Label>
                <Input id="fv-dob" type="date" {...form.register('dob')} />
              </div>
              <div className="flex flex-col gap-vt-1 md:col-span-2">
                <Label htmlFor="fv-current-address">Current address (near society)</Label>
                <Textarea
                  id="fv-current-address"
                  rows={2}
                  placeholder="Where they currently stay — e.g. nearby staff quarters / slum"
                  {...form.register('current_address')}
                />
              </div>
              <div className="flex flex-col gap-vt-1 md:col-span-2">
                <Label htmlFor="fv-permanent-address">Permanent / hometown address</Label>
                <Textarea
                  id="fv-permanent-address"
                  rows={2}
                  placeholder="Village / town, district, state"
                  {...form.register('permanent_address')}
                />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="fv-emer-name">Emergency contact name</Label>
                <Input
                  id="fv-emer-name"
                  placeholder="Spouse / parent / sibling"
                  {...form.register('emergency_contact_name')}
                />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="fv-emer-phone">Emergency contact phone</Label>
                <Input id="fv-emer-phone" placeholder="+91…" {...form.register('emergency_contact_phone')} />
                {form.formState.errors.emergency_contact_phone ? (
                  <p className="text-vt-12 text-vt-alert">
                    {form.formState.errors.emergency_contact_phone.message}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          {/* ───── KYC & verification ───── */}
          <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
            <div className="mb-vt-2 flex items-center gap-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              <ShieldCheck className="h-3.5 w-3.5" /> KYC &amp; verification
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-2">
                <Label htmlFor="fv-photo">Profile photo</Label>
                <div className="flex items-center gap-vt-3">
                  {photoPreviewUrl ? (
                    <img
                      src={photoPreviewUrl}
                      alt="Selected photo preview"
                      className="h-14 w-14 shrink-0 rounded-full object-cover ring-1 ring-vt-border"
                    />
                  ) : editingFv ? (
                    <FvAvatar fv={editingFv} size={56} />
                  ) : (
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-vt-primary-100 text-vt-14 font-semibold text-vt-primary">
                      ?
                    </span>
                  )}
                  <Input
                    id="fv-photo"
                    type="file"
                    accept={FV_IMAGE_ACCEPT}
                    onChange={onPhotoFileChange}
                  />
                </div>
                <p className="text-vt-11 text-vt-text-muted">JPEG / PNG / WebP.</p>
              </div>

              <div className="flex flex-col gap-vt-2">
                <Label htmlFor="fv-idproof-type">ID proof type</Label>
                <Select id="fv-idproof-type" {...form.register('id_proof_type')}>
                  <option value="">— None —</option>
                  {ID_PROOF_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {ID_PROOF_LABEL[t]}
                    </option>
                  ))}
                </Select>
                <Label htmlFor="fv-idproof-last4">ID number (last 4)</Label>
                <Input
                  id="fv-idproof-last4"
                  placeholder="1234"
                  maxLength={8}
                  {...form.register('id_proof_last4')}
                />
                <Label htmlFor="fv-idproof-file">ID proof scan</Label>
                <Input
                  id="fv-idproof-file"
                  type="file"
                  accept={FV_IMAGE_ACCEPT}
                  onChange={onIdProofFileChange}
                />
                {editingFv?.id_proof_storage_key && !idProofFile ? (
                  <p className="text-vt-11 text-vt-text-muted">
                    Existing scan on file{editingFv.id_proof_type ? ` (${ID_PROOF_LABEL[editingFv.id_proof_type]})` : ''}. Upload to replace.
                  </p>
                ) : (
                  <p className="text-vt-11 text-vt-text-muted">
                    Full ID number is never stored — only the last 4 digits and a scan.
                  </p>
                )}
              </div>
            </div>

            {(() => {
              const currentKind = form.watch('kind') as Kind;
              const level: VerificationLevel = levelByCode[currentKind] ?? 'light';
              const kindRow = kindItems.find((k) => k.code === currentKind);
              const hardBlock = Boolean(kindRow?.hard_block_id_card);
              // Surface the gate-entry mode: QR-card vs manual (phone + name +
              // photo + paper ID). For "no card" kinds the gate guard verifies
              // the helper manually before marking IN/OUT.
              const noCard = kindRow?.issue_id_card === false;
              const manualWithPhoto = kindRow?.manual_capture_photo !== false;
              return (
                <div
                  className={`mt-vt-3 rounded-vt-md border px-vt-3 py-vt-2 text-vt-12 ${
                    level === 'recommended'
                      ? 'border-vt-warn bg-vt-warn-100 text-vt-text'
                      : 'border-vt-border bg-vt-surface-2 text-vt-text-muted'
                  }`}
                >
                  <span className="font-semibold">
                    {level === 'recommended' ? 'High-trust role.' : 'Light-touch role.'}
                  </span>{' '}
                  {VERIFICATION_HINT[level]}{' '}
                  {noCard
                    ? manualWithPhoto
                      ? 'No QR ID card — gate entry is manual: guard verifies photo + phone + paper ID before marking IN/OUT.'
                      : 'No QR ID card — gate entry is manual: guard verifies phone + paper ID before marking IN/OUT.'
                    : hardBlock
                      ? 'ID card will not be issued until police verification is verified.'
                      : 'KYC fields below are optional — no hard block on ID-card issuance.'}
                </div>
              );
            })()}

            <div className="mt-vt-3 grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-2">
                <Label htmlFor="fv-police-file">Police verification doc</Label>
                <Input
                  id="fv-police-file"
                  type="file"
                  accept={FV_DOC_ACCEPT}
                  onChange={onPoliceFileChange}
                />
                <p className="text-vt-11 text-vt-text-muted">JPEG / PNG / WebP / PDF.</p>
              </div>
              <div className="flex flex-col gap-vt-2">
                <Label htmlFor="fv-police-decision">Decision</Label>
                <Select
                  id="fv-police-decision"
                  value={policeDecision}
                  onChange={(e) => setPoliceDecision(e.target.value as typeof policeDecision)}
                  disabled={!policeDocFile}
                >
                  <option value="pending">Pending (under review)</option>
                  <option value="verified">Verified</option>
                  <option value="rejected">Rejected</option>
                </Select>
                {editingFv ? (
                  <div className="flex items-center gap-vt-2">
                    <span className="text-vt-11 text-vt-text-muted">Current:</span>
                    <Badge variant={POLICE_VARIANT[editingFv.police_verification_status]}>
                      {POLICE_LABEL[editingFv.police_verification_status]}
                    </Badge>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Input id="notes" placeholder="e.g. only Mon/Wed/Fri" {...form.register('notes')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={create.isPending || update.isPending}
              loadingText={editingId ? 'Saving…' : 'Adding…'}
            >
              {editingId ? 'Save changes' : 'Add frequent visitor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <PageHeader
        title="Frequent visitors"
        description="Recurring service staff (maids, cooks, drivers). Verify nominations, issue ID cards, manage police KYC."
        actions={formDialog}
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-4">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-warn-100)] text-[color:var(--vt-warn)]">
                <Sparkles className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Awaiting review</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-12" /> : pendingCount}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-warn-100)] text-[color:var(--vt-warn)]">
                <AlertTriangle className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Police pending</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-12" /> : policePendingCount}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]">
                <UserCheck className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Verified</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-12" /> : verifiedCount}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <Briefcase className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">On register</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-12" /> : items.length}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tab strip */}
        <div className="flex items-center gap-vt-1 border-b border-vt-border">
          {(['all', 'queue'] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setViewTab(t)}
              className={
                viewTab === t
                  ? 'border-b-2 border-vt-primary px-vt-3 py-vt-2 text-vt-13 font-medium text-vt-text'
                  : 'border-b-2 border-transparent px-vt-3 py-vt-2 text-vt-13 text-vt-text-muted hover:text-vt-text'
              }
            >
              {t === 'all' ? 'All staff' : `Verification queue (${pendingCount + policePendingCount})`}
            </button>
          ))}
        </div>

        {/* Table */}
        <Card>
          <CardContent className="space-y-vt-3 p-vt-4">
            <div className="flex flex-wrap items-center justify-between gap-vt-3">
              <div className="flex items-center gap-vt-2">
                <Filter className="h-4 w-4 text-vt-text-soft" />
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as Status | '')}
                  className="w-44"
                  disabled={viewTab === 'queue'}
                >
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="verified">Verified</option>
                  <option value="rejected">Rejected</option>
                  <option value="expired">Expired</option>
                </Select>
              </div>
              <span className="text-vt-12 text-vt-text-muted">
                {list.data ? `${items.length} record${items.length === 1 ? '' : 's'}` : 'Loading…'}
              </span>
            </div>

            {list.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : list.isError ? (
              <EmptyState
                icon={<UserCheck className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load list"
                description={getErrorMessage(list.error)}
                action={
                  <Button variant="secondary" onClick={() => void list.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : items.length === 0 ? (
              <EmptyState
                icon={<UserCheck className="h-6 w-6" strokeWidth={1.75} />}
                title={viewTab === 'queue' ? 'Queue clear' : 'No nominations yet'}
                description={
                  viewTab === 'queue'
                    ? 'Nothing waiting for review right now.'
                    : 'When residents nominate a recurring helper, their KYC requests will appear here for admin review.'
                }
                action={
                  viewTab === 'queue' ? undefined : (
                    <Button onClick={() => setOpen(true)}>
                      <Plus className="h-4 w-4" /> Add first staff
                    </Button>
                  )
                }
              />
            ) : (
              <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Person</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Police</TableHead>
                    <TableHead>ID card</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((fv) => (
                    <TableRow
                      key={fv.id}
                      className="cursor-pointer hover:bg-vt-surface-2"
                      onClick={(e) => {
                        // Don't open drill-down when clicking action buttons
                        // OR the name <a> tag (which routes to the detail page).
                        const target = e.target as HTMLElement;
                        if (target.closest('button') || target.closest('a')) return;
                        setSelectedFvId(fv.id);
                      }}
                    >
                      <TableCell>
                        <div className="flex items-center gap-vt-3">
                          <FvAvatar fv={fv} size={32} />
                          <div className="min-w-0">
                            {/*
                              Name cell links to the 360° profile at
                              /frequent-visitors/$id. The rest of the row still
                              opens the inline flat-assignments drill-down so
                              admins can manage links without leaving the list.
                            */}
                            <Link
                              to="/frequent-visitors/$id"
                              params={{ id: fv.id }}
                              className="block truncate font-medium text-vt-text hover:text-vt-primary hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {fv.full_name}
                            </Link>
                            <div className="truncate text-vt-12 text-vt-text-muted">{fv.phone}</div>
                          </div>
                          <Link2 className="ml-vt-2 h-3.5 w-3.5 text-vt-text-soft" />
                        </div>
                      </TableCell>
                      <TableCell className="text-vt-text-muted">
                        {labelByCode[fv.kind] ?? fv.kind}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[fv.status]}>
                          {fv.status.charAt(0).toUpperCase() + fv.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={POLICE_VARIANT[fv.police_verification_status]}>
                          {POLICE_LABEL[fv.police_verification_status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {fv.id_card_issued_at ? (
                          <Badge variant="ok">Issued</Badge>
                        ) : (
                          <Badge variant="muted">Not issued</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-vt-text-muted">{formatDateTime(fv.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-vt-2">
                          {fv.status === 'pending' ? (
                            <>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => decide.mutate({ id: fv.id, decision: 'verify' })}
                                disabled={decide.isPending}
                              >
                                <Check className="h-4 w-4" /> Verify
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setRejectTarget(fv)}
                                disabled={decide.isPending}
                              >
                                <X className="h-4 w-4" /> Reject
                              </Button>
                            </>
                          ) : null}
                          {fv.status === 'verified' &&
                          fv.police_verification_status === 'pending' &&
                          fv.police_verification_storage_key ? (
                            <>
                              <Button
                                size="sm"
                                variant="secondary"
                                title="Mark police verified"
                                disabled={markPolice.isPending}
                                onClick={() => markPolice.mutate({ id: fv.id, decision: 'verified' })}
                              >
                                <ShieldCheck className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                title="Mark police rejected"
                                disabled={markPolice.isPending}
                                onClick={() => markPolice.mutate({ id: fv.id, decision: 'rejected' })}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          ) : null}
                          {fv.status === 'verified' ? (
                            (() => {
                              // Some kinds (milk / newspaper / …) opt out of
                              // QR ID cards via the catalog. For those, gate
                              // entry is manual; we disable the row action and
                              // surface the policy in the tooltip.
                              const kindRow = kindItems.find((k) => k.code === fv.kind);
                              const noCard = kindRow?.issue_id_card === false;
                              return (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  title={
                                    noCard
                                      ? `${kindRow?.label ?? fv.kind} doesn't issue ID cards — gate entry is manual (phone + name + photo + paper ID).`
                                      : fv.id_card_issued_at
                                        ? 'Re-issue ID card'
                                        : 'Issue ID card'
                                  }
                                  disabled={issueIdCard.isPending || noCard}
                                  className={noCard ? 'opacity-40' : undefined}
                                  onClick={() => {
                                    if (noCard) return;
                                    issueIdCard.mutate({
                                      id: fv.id,
                                      regenerate: Boolean(fv.id_card_issued_at)
                                    });
                                  }}
                                >
                                  <BadgeCheck className="h-4 w-4" />
                                </Button>
                              );
                            })()
                          ) : fv.status === 'pending' ? (
                            // Show a disabled hint so it's obvious WHY the
                            // ID-card button is missing for older nominated
                            // (resident-pushed) FVs — admin needs to Verify
                            // the row first using the ✓ button to the left.
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Verify this FV first to enable ID card issuance"
                              disabled
                              className="opacity-40"
                            >
                              <BadgeCheck className="h-4 w-4" />
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Edit"
                            onClick={() => {
                              setEditingId(fv.id);
                              setOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={remove.isPending}
                            title="Remove"
                            onClick={() => setRemoveTarget(fv)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <CursorPaginator
                hasMore={Boolean(fvPaginator.nextCursor)}
                loading={list.isFetching}
                itemsShown={items.length}
                pageSize={50}
                onLoadMore={() => {
                  if (fvPaginator.nextCursor) setFvCursor(fvPaginator.nextCursor);
                }}
              />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ─── Drill-down: FV's linked flats ─── */}
      <Dialog open={Boolean(selectedFvId)} onOpenChange={(v) => (v ? null : closeDrillDown())}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {selectedFv ? (
                <span className="flex items-center gap-vt-2">
                  <FvAvatar fv={selectedFv} size={32} />
                  {selectedFv.full_name}
                  <Badge variant="muted" className="capitalize">
                    {labelByCode[selectedFv.kind] ?? selectedFv.kind}
                  </Badge>
                  <Badge variant={STATUS_VARIANT[selectedFv.status]}>{selectedFv.status}</Badge>
                </span>
              ) : null}
            </DialogTitle>
            <DialogDescription>
              {selectedFv?.status === 'verified'
                ? 'Manage which flats this staff member is linked to. Add days/time windows per flat.'
                : 'Staff must be verified before they can be linked to flats.'}
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between border-b border-vt-border pb-vt-2">
            <div className="flex items-center gap-vt-3">
              <span className="text-vt-13 font-semibold text-vt-text-muted">
                Linked flats ({assignmentsQuery.data?.items.length ?? 0})
              </span>
              {(() => {
                const pending =
                  assignmentsQuery.data?.items.filter(
                    (a) => a.approval_status === 'pending_resident'
                  ).length ?? 0;
                return pending > 0 ? (
                  <Badge variant="warn">{pending} awaiting resident</Badge>
                ) : null;
              })()}
            </div>
            <Button
              size="sm"
              disabled={selectedFv?.status !== 'verified'}
              title={selectedFv?.status !== 'verified' ? 'Verify staff first' : 'Add assignment'}
              onClick={() => {
                assignForm.reset({ unit_id: '', days_of_week: '', time_start: '', time_end: '' });
                setSelectedDays([]);
                setAssignOpen(true);
              }}
            >
              <Plus className="h-4 w-4" /> Add assignment
            </Button>
          </div>

          {assignmentsQuery.isLoading ? (
            <div className="flex flex-col gap-vt-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (assignmentsQuery.data?.items.length ?? 0) === 0 ? (
            <EmptyState
              icon={<Link2 className="h-6 w-6" strokeWidth={1.75} />}
              title="No flats linked yet"
              description={
                selectedFv?.status === 'verified'
                  ? 'Click "Add assignment" to link this staff member to one or more flats.'
                  : 'Verify this staff member first to enable flat linking.'
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Flat</TableHead>
                  <TableHead>Schedule</TableHead>
                  <TableHead>Added by</TableHead>
                  <TableHead>
                    <div className="flex items-center gap-vt-2">
                      <span>Status</span>
                      <Select
                        value={approvalFilter}
                        onChange={(e) => setApprovalFilter(e.target.value as ApprovalStatus | '')}
                        className="h-7 w-36 text-vt-11"
                      >
                        <option value="">All approvals</option>
                        <option value="auto_approved">Auto-approved</option>
                        <option value="approved">Approved</option>
                        <option value="pending_resident">Pending resident</option>
                        <option value="rejected">Rejected</option>
                      </Select>
                    </div>
                  </TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(assignmentsQuery.data?.items ?? [])
                  .filter((a) => (approvalFilter ? a.approval_status === approvalFilter : true))
                  .map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">
                      {a.unit.block?.name ?? '—'} / {a.unit.number}
                    </TableCell>
                    <TableCell className="text-vt-13 text-vt-text-muted">
                      <div className="flex flex-wrap items-center gap-vt-2">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {a.days_of_week === null
                          ? 'Any day'
                          : a.days_of_week === 'all'
                          ? 'All days'
                          : a.days_of_week
                              .split(',')
                              .map((d) => DAY_LABEL[d] ?? d)
                              .join(' · ')}
                        {a.time_start && a.time_end ? (
                          <>
                            <Clock3 className="ml-vt-1 h-3.5 w-3.5" />
                            {a.time_start}–{a.time_end}
                          </>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-vt-13 text-vt-text-muted">
                      {a.added_by?.full_name ?? <span className="text-vt-text-soft">Admin</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-vt-1">
                        {a.active ? <Badge variant="ok">Active</Badge> : <Badge variant="muted">Inactive</Badge>}
                        <Badge variant={APPROVAL_STATUS_VARIANT[a.approval_status]}>
                          {APPROVAL_STATUS_LABEL[a.approval_status]}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-vt-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          title={a.active ? 'Deactivate' : 'Activate'}
                          disabled={toggleActive.isPending}
                          onClick={() => toggleActive.mutate({ aid: a.id, active: !a.active })}
                        >
                          {a.active ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={removeAssignment.isPending}
                          title="Remove assignment"
                          onClick={() => setRemoveAssignmentTarget(a)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeDrillDown}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Add assignment (nested) ─── */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link {selectedFv?.full_name ?? 'staff'} to a flat</DialogTitle>
            <DialogDescription>
              Choose the flat + optional days/time window. Leave days empty for any day.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={assignForm.handleSubmit((v) => createAssignment.mutate(v))}
            className="flex flex-col gap-vt-4"
          >
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="unit_id">Flat</Label>
              <Select
                id="unit_id"
                disabled={unitsQuery.isLoading}
                aria-invalid={Boolean(assignForm.formState.errors.unit_id) || undefined}
                {...assignForm.register('unit_id')}
              >
                <option value="">{unitsQuery.isLoading ? 'Loading flats…' : 'Select flat'}</option>
                {unitOptions.map((u) => (
                  <option key={u.id} value={u.id}>{u.label}</option>
                ))}
              </Select>
              {assignForm.formState.errors.unit_id ? (
                <p className="text-vt-12 text-vt-alert" role="alert">{assignForm.formState.errors.unit_id.message}</p>
              ) : null}
            </div>

            <div className="flex flex-col gap-vt-2">
              <Label>Days of week (optional)</Label>
              {/*
                Multi-select chip row — visually aligned to TabsPills (canonical
                pill primitive) but kept inline because TabsPills is single-select
                by API. Classes mirror the active/inactive styling of the canonical
                primitive (incl. !text-white on active) to avoid drift.
              */}
              <div role="group" aria-label="Days of week" className="flex flex-wrap items-center gap-vt-2">
                {DAYS_OF_WEEK.map((d) => {
                  const on = selectedDays.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleDay(d)}
                      className={
                        'inline-flex h-7 items-center justify-center whitespace-nowrap rounded-vt-pill px-vt-3 py-1 text-vt-12 font-medium transition-[background-color,color,box-shadow] duration-vt ease-vt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-vt-bg ' +
                        (on
                          ? 'bg-vt-primary !text-white shadow-vt-1 hover:bg-vt-primary-600'
                          : 'bg-vt-surface-2 text-vt-text hover:bg-vt-surface-3')
                      }
                    >
                      {DAY_LABEL[d]}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() =>
                    setSelectedDays(selectedDays.length === 7 ? [] : [...DAYS_OF_WEEK])
                  }
                  className="ml-vt-2 inline-flex h-7 items-center justify-center rounded-vt-pill border border-vt-border bg-transparent px-vt-3 py-1 text-vt-12 font-medium text-vt-text-muted transition-colors hover:bg-vt-surface-2 hover:text-vt-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400"
                >
                  {selectedDays.length === 7 ? 'Clear' : 'All'}
                </button>
              </div>
              <p className="text-vt-12 text-vt-text-soft">
                {selectedDays.length === 0
                  ? 'Any day allowed (no day filter)'
                  : selectedDays.length === 7
                  ? 'All days'
                  : selectedDays.map((d) => DAY_LABEL[d]).join(' · ')}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-vt-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="time_start">From (optional)</Label>
                <Input id="time_start" type="time" {...assignForm.register('time_start')} />
                {assignForm.formState.errors.time_start ? (
                  <p className="text-vt-12 text-vt-alert" role="alert">{assignForm.formState.errors.time_start.message}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="time_end">To (optional)</Label>
                <Input id="time_end" type="time" {...assignForm.register('time_end')} />
                {assignForm.formState.errors.time_end ? (
                  <p className="text-vt-12 text-vt-alert" role="alert">{assignForm.formState.errors.time_end.message}</p>
                ) : null}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setAssignOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={createAssignment.isPending} loadingText="Linking…">
                Link flat
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── ID-card issued: full card preview + download ─── */}
      <Dialog
        open={Boolean(idCardFvId && idCardData)}
        onOpenChange={(v) => {
          if (!v) {
            setIdCardFvId(null);
            setIdCardData(null);
            if (idCardPreviewUrl) {
              URL.revokeObjectURL(idCardPreviewUrl);
              setIdCardPreviewUrl(null);
            }
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>ID card</DialogTitle>
            <DialogDescription>
              Card with profile + assigned flat + permanent QR. Print or share with the staff member.
            </DialogDescription>
          </DialogHeader>
          {idCardData ? (
            <div className="flex flex-col items-center gap-vt-3 py-vt-3">
              {idCardPreviewUrl ? (
                <img
                  src={idCardPreviewUrl}
                  alt="Frequent visitor full ID card"
                  className="w-full max-w-[600px] rounded-vt-md border border-vt-border bg-white"
                />
              ) : (
                // Fallback while the SVG fetch is in flight or failed.
                <img
                  src={idCardData.qr_data_url}
                  alt="Frequent visitor ID card QR"
                  className="h-64 w-64 rounded-vt-md border border-vt-border bg-white p-vt-2"
                />
              )}
              <span className="text-vt-12 text-vt-text-muted">
                Valid 365 days. Re-issue from row action to revoke + regenerate.
              </span>
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIdCardFvId(null);
                setIdCardData(null);
                if (idCardPreviewUrl) {
                  URL.revokeObjectURL(idCardPreviewUrl);
                  setIdCardPreviewUrl(null);
                }
              }}
            >
              Close
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                if (!idCardFvId) return;
                const fv = items.find((i) => i.id === idCardFvId);
                void downloadIdCard(idCardFvId, fv?.full_name ?? 'staff', 'png');
              }}
              title="Bare QR PNG (legacy / kiosk scanners)"
            >
              <Download className="h-4 w-4" /> QR only
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (!idCardFvId) return;
                const fv = items.find((i) => i.id === idCardFvId);
                void downloadIdCard(idCardFvId, fv?.full_name ?? 'staff', 'svg');
              }}
              title="Full card with photo, name, flat, QR — recommended for printing"
            >
              <Download className="h-4 w-4" /> Download card
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(rejectTarget)}
        onOpenChange={(v) => (v ? null : setRejectTarget(null))}
        title="Reject frequent visitor"
        description={
          rejectTarget ? (
            <>
              Reject the registration for <strong>{rejectTarget.full_name}</strong>? Provide a
              reason so the resident who nominated them sees context.
            </>
          ) : null
        }
        confirmLabel="Reject"
        variant="warn"
        promptLabel="Reason (optional)"
        promptPlaceholder="e.g. ID proof unclear — please re-upload"
        loading={decide.isPending}
        onConfirm={(reason) => {
          if (!rejectTarget) return;
          decide.mutate(
            { id: rejectTarget.id, decision: 'reject', reason: reason?.trim() || undefined },
            { onSuccess: () => setRejectTarget(null) }
          );
        }}
      />

      <ConfirmDialog
        open={Boolean(removeTarget)}
        onOpenChange={(v) => (v ? null : setRemoveTarget(null))}
        title="Remove frequent visitor"
        description={
          removeTarget ? (
            <>
              Remove <strong>{removeTarget.full_name}</strong> from the frequent-visitor register?
              Their ID card stops working at the gate.
            </>
          ) : null
        }
        confirmLabel="Remove"
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (!removeTarget) return;
          remove.mutate(removeTarget.id, { onSuccess: () => setRemoveTarget(null) });
        }}
      />

      <ConfirmDialog
        open={Boolean(removeAssignmentTarget)}
        onOpenChange={(v) => (v ? null : setRemoveAssignmentTarget(null))}
        title="Remove flat assignment"
        description={
          selectedFv && removeAssignmentTarget ? (
            <>
              Unlink this flat from <strong>{selectedFv.full_name}</strong>? They lose entry
              permission for this unit.
            </>
          ) : (
            'Unlink this flat from the frequent visitor?'
          )
        }
        confirmLabel="Remove assignment"
        variant="danger"
        loading={removeAssignment.isPending}
        onConfirm={() => {
          if (!removeAssignmentTarget) return;
          removeAssignment.mutate(removeAssignmentTarget.id, {
            onSuccess: () => setRemoveAssignmentTarget(null)
          });
        }}
      />
    </>
  );
}
